import { registerSW } from 'virtual:pwa-register';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';

import App from './App.tsx';
import { Notifications } from '@/components/notification/Notifications.tsx';
import i18n from '../src/i18n';
import theme from './theme';

import './index.css';

LicenseInfo.setLicenseKey(
  // TODO ez jojjon env-bol
  'f331807687cd3dc9d06f72b7af5c100cTz0xMjMzMzcsRT0xNzk3NjM4Mzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
);

const origUrl = location.href;
const updateSW = registerSW({
  immediate: false,
  // this can be removed when reload is fixed in flutter
  onNeedRefresh: async function () {
    // this causes a short white screen
    document.getElementById('root')!.textContent = '';
    history.replaceState({}, '', origUrl);
    await updateSW(true);
  },
});

// https://vite.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

if (USE_SENTRY) {
  Sentry.init({
    // TODO ez jojjon env-bol
    dsn: 'https://2059a9a107511d66dc29953f9b2b9f6a@o4507889544593408.ingest.de.sentry.io/4507901161898064',
    release: VERSION,
    environment: ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.replayIntegration(),
    ],

    // tracesSampleRate is set to 0 because we're using tracesSampler instead.
    // When tracesSampler is defined, it fully overrides tracesSampleRate.
    // This makes sampling logic centralized and more flexible.
    tracesSampleRate: 0.0,

    tracesSampler: (ctx) => {
      // Page load transactions are typically the largest and most frequent.
      // Sampling only 5% significantly reduces Sentry traffic and rate limit risk
      if (ctx.transactionContext?.op === 'pageload') return 0.05;
      // Capture 20% of the transactions (devel, testing)
      if (ENVIRONMENT !== 'stable') return 0.2;
      // Capture 10% of the transactions (stable)
      return 0.1;
    },

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/pluto3-devel\.itrack\.hu/, /^https:\/\/pluto3-devel\.itrack\.hu\/api/],

    // Session Replay records the user's interaction with the app (DOM, clicks, inputs, etc.).
    // We're not using Sentry Replay because session insights are monitored through Clarity.
    // Disabled (0.0) to reduce network traffic and avoid unnecessary event volume.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,

    ignoreErrors: [
      'Invalid LatLng object: (NaN, NaN)',
      'Request aborted',
      'timeout of 60000ms exceeded',
      'Network Error',
      'CanceledError: canceled',
      //Axios HTTP status error: 401–404, 500–504
      /^Request failed with status code (40[1-4]|50[0-4])$/,
      //Saját PlutoClientException hibák
      /^PlutoClientException\b/,
    ],
  });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onError: (_error, _query) => {
      // console.debug(error, query);
    },
  }),
  mutationCache: new MutationCache({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onError: (_error, _variables, _context, _mutation) => {
      // console.debug(error, variables, context, mutation);
    },
  }),
});

ReactDOM.createRoot(
  document.getElementById('root')! /*, {
  onUncaughtError: (error, errorInfo) => {
    console.log(error, errorInfo);
  },
  onCaughtError: (error, errorInfo) => {
    console.log(error, errorInfo);
  },
}*/
).render(
  // <React.StrictMode>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <I18nextProvider i18n={i18n}>
        <Notifications />
        <App />
      </I18nextProvider>
    </ThemeProvider>
  </QueryClientProvider>
  // </React.StrictMode>
);
